import React, { memo, useMemo } from "react";
import t from "../../../../../../../../../i18n";
import { useGUIEditorContext } from "../../../../../../hook/context/gui";
import { uniqBy } from "diagnostics-lib/array";
import { MarkerInfoSelect } from "./MarkerInfoSelect";
import { useAvailableResources } from "../../../../../../hook/useAvailableResources";
import { getAvailableResourceKind } from "../../../../../../utils/getAvailableResourceKind";

export const DisplaySetting = memo(({ projectId }: { projectId: string }) => {
  const { block, preset } = useGUIEditorContext();
  const availableResources = useAvailableResources({ projectId });

  const scatterDisplayOptions = useMemo(() => {
    const encodings =
      block?.config.layers.flatMap(({ encoding }) => [encoding.x, encoding.y]) ?? [];

    const quantityEncodings = uniqBy(
      encodings.filter(
        encoding =>
          getAvailableResourceKind(availableResources, encoding.identifier) === "QUANTITY",
      ),
      "identifier",
    );

    const faultEncodings = uniqBy(
      encodings.filter(
        encoding => getAvailableResourceKind(availableResources, encoding.identifier) === "FAULT",
      ),
      "identifier",
    );

    const options = [
      {
        label: t("project.dashboard.resource.column", "date"),
        value: "date",
      },
      {
        label: t("project.dashboard.resource.column", "hour"),
        value: "hour",
      },
      {
        label: t("project.dashboard.resource.column", "dayOfYear"),
        value: "dayOfYear",
      },
      {
        label: t("project.dashboard.resource.column", "group"),
        value: "group",
      },
      ...(quantityEncodings.length > 0 &&
      quantityEncodings.some(e => e.column === "measured" || e.column === "std")
        ? quantityEncodings
            .filter(e => e.column === "measured" || e.column === "std")
            .map(encoding => ({
              label: `${t("project.dashboard.resource.column", "validity")} — ${encoding.identifier}`,
              value: `validity-${encoding.identifier}`,
              disabled: false,
            }))
        : [
            {
              label: t("project.dashboard.resource.column", "validity"),
              value: "validity",
              disabled: true,
            },
          ]),
      ...(faultEncodings.length > 0
        ? faultEncodings.map(encoding => ({
            label: `${t("project.dashboard.resource.column", "faultStatus")} — ${encoding.identifier}`,
            value: `faultStatus-${encoding.identifier}`,
            disabled: false,
          }))
        : [
            {
              label: t("project.dashboard.resource.column", "faultStatus"),
              value: "faultStatus",
              disabled: true,
            },
          ]),
    ];
    return options;
  }, [availableResources, block?.config.layers]);

  const lineDisplayOptions = useMemo(() => {
    const encodings =
      block?.config.layers.flatMap(layer => [layer.encoding.x, layer.encoding.y]) ?? [];

    return [
      {
        label: t("project.dashboard.resource.column", "validity"),
        value: "validity",
        disabled: encodings.every(
          encoding =>
            getAvailableResourceKind(availableResources, encoding.identifier) !== "QUANTITY" ||
            (encoding.column !== "measured" && encoding.column !== "std"),
        ),
      },
      {
        label: t("project.dashboard.resource.column", "faultStatus"),
        value: "faultStatus",
        disabled: encodings.every(
          encoding => getAvailableResourceKind(availableResources, encoding.identifier) !== "FAULT",
        ),
      },
    ];
  }, [availableResources, block?.config.layers]);

  if (preset !== "line" && preset !== "scatter") return null;

  return (
    <MarkerInfoSelect
      projectId={projectId}
      options={preset === "line" ? lineDisplayOptions : scatterDisplayOptions}
    />
  );
});
